import { memo, useState } from 'react'
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import { useShallow } from 'zustand/react/shallow'
import { camelToKebabCase } from '../../../../../helpers/functions/camel-to-kebab'
import {
  useTranslate,
  type Language,
} from '../../../../../hooks/language/use-translate'
import { useGetGamesBoxesRoomsDetailsCount } from '../../../../../hooks/queries/games/games-boxes-rooms-details/use-get-games-boxes-rooms-details-count'
import { useGetGamesBoxesRoomsHistory } from '../../../../../hooks/queries/games/games-boxes-rooms/use-get-games-boxes-rooms-history'
import type { GameBoxRoom } from '../../../../../services/games/games-boxes-rooms/types'
import { useGlobalStore } from '../../../../../store/global-store'
import { ModalHistoryDetails } from './details'

interface Props {
  handleBack: () => void
}

function History({
  gameBoxRoom,
  onClick,
}: {
  gameBoxRoom: GameBoxRoom
  onClick?: () => void
}) {
  const { gamesBoxesRoomsDetailsCount } = useGetGamesBoxesRoomsDetailsCount({
    gameBoxRoomId: gameBoxRoom?.id,
  })

  const { translate } = useTranslate()

  return (
    <div
      key={gameBoxRoom.id}
      // onClick={() => {
      //   setSelectedGameBox(gameBoxRoom)
      //   setShowDetail(true)
      // }}
      onClick={onClick}
      className="flex flex-col w-full p-2 bg-white border border-gray-200 rounded-sm"
    >
      <div className="mb-2">
        <span className="text-[10px] leading-none text-gray-500">
          {gameBoxRoom?.dateInitialDistribute}{' '}
          {translate('modal-description-register-conditions')}{' '}
          {translate(
            `modal-condition-${camelToKebabCase(gameBoxRoom?.typeGameBoxSent)}` as Language,
          )}
        </span>
      </div>

      <div className="flex flex-row mb-2">
        <span className="text-4xl">🎁</span>

        <div className="ml-2">
          <span className="text-sm text-gray-800">
            {translate('modal-description-register-equal-division')}
          </span>

          <div className="flex flex-row gap-1">
            <div className="flex flex-row px-2  gap-1 bg-red-100 rounded-2xl items-center justify-center">
              <span className="text-[8px]">🎁</span>
              <span className="text-[8px] text-red-600 font-bold">
                {gameBoxRoom?.quantityPeopleEarning}
              </span>
            </div>

            <div className="flex flex-row px-2 gap-1 bg-amber-100 rounded-2xl items-center justify-center">
              <span className="text-[8px]">🪙</span>

              <span className="text-[8px] text-amber-600 font-bold">
                {gameBoxRoom?.valueTotalDistribute *
                  gameBoxRoom?.quantityPeopleEarning}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col flex-1 justify-center items-end">
          <div className="flex flex-col">
            <div className="flex flex-row gap-2">
              <span className="text-[10px]">
                {translate(
                  'modal-description-register-people-received',
                  gamesBoxesRoomsDetailsCount?.count || 0,
                )}
              </span>
              <FaArrowRight className="text-gray-400" />
            </div>

            <span className="text-[8px] text-gray-400">
              {translate(
                'modal-description-register-return',
                gameBoxRoom?.valueTotalDistribute *
                  gameBoxRoom?.quantityPeopleEarning,
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export const ModalHistory = memo(({ handleBack }: Props) => {
  const { liveId } = useGlobalStore(
    useShallow((state) => ({
      liveId: state.liveId,
    })),
  )

  const [selectedGameBox, setSelectedGameBox] = useState<GameBoxRoom | null>(
    null,
  )
  const [showDetail, setShowDetail] = useState(false)

  const { gamesBoxesRooms } = useGetGamesBoxesRoomsHistory({
    liveId: liveId!,
  })

  const { translate } = useTranslate()

  if (showDetail && selectedGameBox) {
    return (
      <ModalHistoryDetails
        selectedGameBox={selectedGameBox}
        handleBack={() => {
          setSelectedGameBox(null)
          setShowDetail(false)
        }}
      />
    )
  }

  return (
    <div className="w-screen h-screen flex flex-col z-50">
      <div className="bg-white h-full rounded-t-3xl py-5 flex flex-col overflow-y-auto">
        <div className="flex justify-center gap-8 mb-4 relative border-b border-b-gray-200">
          <FaArrowLeft
            className="absolute top-1 left-4 text-gray-400"
            onClick={handleBack}
          />

          <span className="pb-2 font-semibold text-gray-500">
            {translate('modal-description-register')}
          </span>
        </div>

        <div className="px-4">
          <span className="text-xs text-gray-500">
            {translate('modal-description-register-seven-days')}
          </span>

          <div className="overflow-y-scroll overflow-x-hidden mt-2 flex flex-col gap-2">
            {gamesBoxesRooms?.map((gameBoxRoom) => (
              <History
                key={gameBoxRoom.id}
                gameBoxRoom={gameBoxRoom}
                onClick={() => {
                  setSelectedGameBox(gameBoxRoom)
                  setShowDetail(true)
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
})

ModalHistory.displayName = 'ModalHistory'
